import { useState, useCallback } from 'react'

/**
 * Storage keys for Data Catalog state persistence
 * Namespaced to avoid collisions with other features
 */
export const DATA_CATALOG_STORAGE_KEYS = {
  page: 'data-catalog:page',
  pageSize: 'data-catalog:pageSize',
  type: 'data-catalog:type',
  uploader: 'data-catalog:uploader',
  search: 'data-catalog:search',
  sortBy: 'data-catalog:sortBy',
  sortDir: 'data-catalog:sortDir',
  // V2 consolidated-filter additions (Figma 13331:47890) — Status +
  // Creation Date filters surfaced from the new cascade menu. Filtered
  // client-side for now since listDocsPaginated doesn't accept these
  // yet; once the API gains them the client filter becomes a no-op pass.
  status: 'data-catalog:status',
  creationDate: 'data-catalog:creationDate',
  // Multi-select checkbox state. Stored as a string[] of doc ids (a Set
  // doesn't survive JSON.stringify) so selections persist when navigating
  // into a file's detail view and back.
  selection: 'data-catalog:selection',
  // Doc type captured per selected id at selection time. The list fetch is
  // server-filtered + paginated, so a selected doc can drop out of `documents`
  // once a filter hides it; this lets selection-gated actions still resolve a
  // selected doc's type even when it isn't on the current page.
  selectionTypes: 'data-catalog:selectionTypes',
} as const

/**
 * Hook that works like useState but persists value to sessionStorage
 *
 * @param key - Storage key (use DATA_CATALOG_STORAGE_KEYS for consistency)
 * @param defaultValue - Default value if nothing in storage or validation fails
 * @param validator - Optional function to validate stored value
 * @returns Tuple of [value, setValue] like useState
 *
 * @example
 * ```tsx
 * const [page, setPage] = useSessionState(
 *   DATA_CATALOG_STORAGE_KEYS.page,
 *   1,
 *   (v) => typeof v === 'number' && v >= 1
 * )
 * ```
 */
export function useSessionState<T>(
  key: string,
  defaultValue: T,
  validator?: (value: unknown) => boolean
): [T, (value: T | ((prev: T) => T)) => void] {
  // Read initial value from sessionStorage
  const getInitialValue = (): T => {
    if (typeof window === 'undefined') {
      return defaultValue
    }

    try {
      const raw = sessionStorage.getItem(key)
      if (raw === null) {
        return defaultValue
      }

      const parsed = JSON.parse(raw)

      // Validate if validator provided
      if (validator && !validator(parsed)) {
        return defaultValue
      }

      return parsed as T
    } catch {
      // Handle JSON parse errors or sessionStorage access errors
      return defaultValue
    }
  }

  const [value, setValueInternal] = useState<T>(getInitialValue)

  // Setter that updates both React state and sessionStorage
  const setValue = useCallback(
    (newValue: T | ((prev: T) => T)) => {
      setValueInternal((prev) => {
        const resolved =
          typeof newValue === 'function'
            ? (newValue as (prev: T) => T)(prev)
            : newValue

        // Write to sessionStorage
        try {
          sessionStorage.setItem(key, JSON.stringify(resolved))
        } catch {
          // Silently fail if sessionStorage is not available (private browsing)
        }

        return resolved
      })
    },
    [key]
  )

  return [value, setValue]
}

export default useSessionState
